/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import { sha1HashString } from 'cio/utils/crypto';
import { waitForPromise } from '@ember/test-waiters';

const MAX_HEX = 16 ** 40; // The maximum number of bytes in a SHA1 hash (160 bits, 20 bytes, 40 hex digits)

export enum Variant {
  A = 'variant_a',
  B = 'variant_b',
}

interface UiTesterArgs {
  testId: string;
  aDistribution: number; // % of users that will be presented with "A" variant
  aVariantId: string;
  bVariantId: string;
}

interface VariantData {
  id: string;
  value: Variant;
  distribution: number;
}

export type UsageData = { [index: number]: string };

export default class UiTester extends Component<UiTesterArgs> {
  @service account!: AccountService;
  @service analytics!: AnalyticsService;

  @tracked
  variant!: VariantData;

  constructor(owner: UiTester, args: UiTesterArgs) {
    super(owner, args);

    waitForPromise(this.calcVariant());
  }

  private async calcVariant() {
    // this function returns a random variant based on userId, accountId and testId
    // this variant will be consistent for the same user and same experiment

    // hexHash: random but deterministic hash (the hash will always be the same unless the input string is changed)
    let hexHash = await sha1HashString(
      `${this.account.current.currentUser.id}${this.account.current.id}${this.args.testId}`,
    );
    // factor: float value between 0 and 1
    let factor = parseInt(hexHash, 16) / MAX_HEX;
    let treshold = this.args.aDistribution / 100;
    let variant = factor <= treshold ? Variant.A : Variant.B;

    this.variant =
      variant === Variant.A
        ? {
            id: this.args.aVariantId,
            value: variant,
            distribution: this.args.aDistribution,
          }
        : {
            // Variant B
            id: this.args.bVariantId,
            value: variant,
            distribution: 100 - this.args.aDistribution,
          };

    this.analytics.track('ui_test', this.variant.value, {
      data: {
        testId: this.args.testId,
        variantId: this.variant.id,
        variantDistribution: this.variant.distribution,
        eventType: 'presentation',
      },
    });
  }

  get isVariantA() {
    return this.variant?.value === Variant.A;
  }

  @action
  trackUsage(usageData?: unknown) {
    this.analytics.track('ui_test', this.variant.value, {
      data: {
        testId: this.args.testId,
        variantId: this.variant.id,
        variantDistribution: this.variant.distribution,
        eventType: 'usage',
        usageData,
      },
    });
  }
}
